import { ApprovalStatus, CompletionCondition } from '@insio/shared-types';
import type { BadgeProps } from '@insio/ui-toolkit';
import {
  CANCELLED_BADGE_COLOR,
  COMPLETED_BADGE_COLOR
} from '@/features/procurement/utils/procurement-status';

/**
 * The status an invoice shows: what is expected of it next, not what happened. Derived from
 * `approvalStatus`, `isCancelled`, `isCompleted` and `unmetCompletionConditions`, see {@link invoiceDisplayStatus}.
 */
export const InvoiceDisplayStatus = {
  FOR_EDIT: 'FOR_EDIT',
  FOR_APPROVAL: 'FOR_APPROVAL',
  REJECTED: 'REJECTED',
  /** Approved; the account still requires the posting. */
  TO_POST: 'TO_POST',
  /** Approved; the account still requires the payment. */
  TO_PAY: 'TO_PAY',
  TO_POST_AND_PAY: 'TO_POST_AND_PAY',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED'
} as const;

export type InvoiceDisplayStatus = (typeof InvoiceDisplayStatus)[keyof typeof InvoiceDisplayStatus];

/** Minimal invoice shape needed to derive the displayed status. */
export interface InvoiceDisplayStatusSource {
  readonly approvalStatus?: ApprovalStatus | null;
  readonly isCancelled?: boolean | null;
  readonly isCompleted?: boolean | null;
  /** Only the conditions the account's completion settings require; left out reads as none. */
  readonly unmetCompletionConditions?: readonly CompletionCondition[] | null;
}

/** What an invoice shows while not approved; the automation and a user deciding look alike. */
const SAME_NAMED: ReadonlyMap<string, InvoiceDisplayStatus> = new Map([
  [ApprovalStatus.UNPROCESSED, InvoiceDisplayStatus.FOR_EDIT],
  [ApprovalStatus.PENDING, InvoiceDisplayStatus.FOR_APPROVAL],
  [ApprovalStatus.FOR_REVIEW, InvoiceDisplayStatus.FOR_APPROVAL],
  [ApprovalStatus.REJECTED, InvoiceDisplayStatus.REJECTED]
]);

/** An approved invoice waits for what its unmet conditions name; with neither, it reads completed. */
function approvedDisplayStatus(
  conditions: readonly CompletionCondition[] | null | undefined
): InvoiceDisplayStatus {
  const posting = conditions?.includes(CompletionCondition.NOT_POSTED) ?? false;
  const payment = conditions?.includes(CompletionCondition.NOT_PAID) ?? false;
  if (posting && payment) return InvoiceDisplayStatus.TO_POST_AND_PAY;
  if (posting) return InvoiceDisplayStatus.TO_POST;
  if (payment) return InvoiceDisplayStatus.TO_PAY;
  // Only inconsistent data gets here: nothing left to wait for, yet not completed.
  return InvoiceDisplayStatus.COMPLETED;
}

/** Derive the one status an invoice shows: cancelled › completed › what it waits for. */
export function invoiceDisplayStatus(source: InvoiceDisplayStatusSource): InvoiceDisplayStatus {
  if (source.isCancelled) return InvoiceDisplayStatus.CANCELLED;
  if (source.isCompleted) return InvoiceDisplayStatus.COMPLETED;
  if (source.approvalStatus === ApprovalStatus.APPROVED) {
    return approvedDisplayStatus(source.unmetCompletionConditions);
  }
  // An unknown status — an older row, a newer server — reads as work in hand, not an empty badge.
  return (
    SAME_NAMED.get(source.approvalStatus ?? ApprovalStatus.UNPROCESSED) ??
    InvoiceDisplayStatus.FOR_EDIT
  );
}

/** Badge colour for a displayed status — one map, so an invoice reads the same wherever it shows. */
export const invoiceDisplayStatusColor = (
  status: InvoiceDisplayStatus
): NonNullable<BadgeProps['color']> => DISPLAY_STATUS_COLOR[status] ?? 'neutral';

const DISPLAY_STATUS_COLOR: Readonly<
  Record<InvoiceDisplayStatus, NonNullable<BadgeProps['color']>>
> = {
  [InvoiceDisplayStatus.FOR_EDIT]: 'info',
  [InvoiceDisplayStatus.FOR_APPROVAL]: 'warning',
  [InvoiceDisplayStatus.REJECTED]: 'error',
  // Approved and under way, the colour the list's FOR_EXPORT bucket wears.
  [InvoiceDisplayStatus.TO_POST]: 'success',
  [InvoiceDisplayStatus.TO_PAY]: 'success',
  [InvoiceDisplayStatus.TO_POST_AND_PAY]: 'success',
  [InvoiceDisplayStatus.COMPLETED]: COMPLETED_BADGE_COLOR,
  [InvoiceDisplayStatus.CANCELLED]: CANCELLED_BADGE_COLOR
};
